import type { InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from 'react';
import { cn } from '../cn.js';

export interface FieldProps {
  label?: string;
  hint?: string;
  error?: string;
  /** Right-aligned adornment (e.g. "optional"). */
  aside?: ReactNode;
  className?: string;
}

/** Shared label/hint/error scaffold used by Input and Textarea. */
function Field({
  label,
  hint,
  error,
  aside,
  className,
  children,
}: FieldProps & { children: ReactNode }): JSX.Element {
  return (
    <label className={cn('as-field', error && 'as-field--error', className)}>
      {(label || aside) && (
        <span className="as-field__top">
          {label && <span className="as-field__label">{label}</span>}
          {aside && <span className="as-field__aside">{aside}</span>}
        </span>
      )}
      {children}
      {error ? (
        <span className="as-field__error">{error}</span>
      ) : hint ? (
        <span className="as-field__hint">{hint}</span>
      ) : null}
    </label>
  );
}

export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className'>,
    FieldProps {}

/** A labelled text input. */
export function Input({ label, hint, error, aside, className, ...rest }: InputProps): JSX.Element {
  return (
    <Field label={label} hint={hint} error={error} aside={aside} className={className}>
      <input className="as-input" {...rest} />
    </Field>
  );
}

export interface TextareaProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'className'>,
    FieldProps {}

/** A labelled multi-line input. */
export function Textarea({
  label,
  hint,
  error,
  aside,
  className,
  rows = 4,
  ...rest
}: TextareaProps): JSX.Element {
  return (
    <Field label={label} hint={hint} error={error} aside={aside} className={className}>
      <textarea className="as-input as-textarea" rows={rows} {...rest} />
    </Field>
  );
}
